import { Injectable, inject } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { LOCATION } from "@tylertech/shared-module";
import { ExecutionResult } from "@tylertech/tyler-api-types";
import { CEF_SHARP, CefSharpObject, SHELL, SHELL_ACCESS_PROVIDER, ShellAccessProvider, ShellJavaScriptObject } from "../providerTokens";
import { AlertDialogData, SimpleAlertDialogComponent } from "./simpleAlertDialog.component";
import { getLoggerFor } from "@tylertech/logging";

@Injectable()
export class ShellService
{
    private readonly location = inject<Location>(LOCATION);
    private readonly shell = inject<ShellJavaScriptObject>(SHELL);
    private readonly shellAccessProvider = inject<ShellAccessProvider>(SHELL_ACCESS_PROVIDER);
    private readonly cefSharp = inject<CefSharpObject>(CEF_SHARP);
    private readonly matDialog = inject(MatDialog);

    private readonly log = getLoggerFor("ShellService");

    closeSelfFromShell(navigateToLinkAfterClosing: string): void
    {
        try
        {
            if (this.isEmbedded)
            {
                this.shell.closeSelfFromShell(navigateToLinkAfterClosing);
            }
        }
        catch (e)
        {
            this.log.error(e);
        }

        this.location.reload();
    }

    executeShellFunction<T>(
        functionName: string,
        inputJson: string = null,
        async: boolean = false
    ): Promise<ExecutionResult<T>>
    {
        if (!this.isEmbedded())
        {
            // TODO - why are we lying? shouldn't we return failure with a message about not in a embedded context?
            return Promise.reject({ Success: true });
        }

        return new Promise((
            resolve: (result: ExecutionResult<T>) => void,
            reject: (reason: unknown) => void) =>
        {
            try
            {
                // ? Review: why did we need to wait 3 second before deferring to the Host?
                // setTimeout(
                //     () =>
                //     {
                if (typeof this.shell === "undefined" || !this.shell)
                {
                    reject({ Success: false } as ExecutionResult<T>);
                    return;
                }

                const responseJson = async
                    ? this.shell.executeAsyncFunction(functionName, inputJson)
                    : this.shell.executeFunction(functionName, inputJson);
                const result = JSON.parse(responseJson);
                if (result.Success)
                {
                    resolve(result);
                }
                else
                {
                    reject(result);
                }
                // },
                // 3000);
            }
            catch (error)
            {
                reject({ Success: false } as ExecutionResult<T>);
            }
        });
    }

    isChromium(): boolean
    {
        return !!this.cefSharp;
    }

    /**
     * Designates that the app is running inside of a browser hosted by a Shell.
     *
     * @returns `true` if either the shell or shellAccessProvider objects are defined on the global window object,
     * `false` otherwise
     */
    isEmbedded(): boolean
    {
        return !!this.shell
            || !!this.shellAccessProvider;
    }

    openShellFunction<T>(
        functionName: string,
        inputJson: string = null,
        async: boolean = false,
        errorText: string = ""
    ): Promise<ExecutionResult<T> | void>
    {
        return this.executeShellFunction(functionName, inputJson, async)
            .then(
                (result: ExecutionResult<T>) =>
                {
                    return result;
                },
                (result: ExecutionResult<string>) =>
                {
                    const dialogConfig: MatDialogConfig<AlertDialogData> = {
                        disableClose: false,
                        data: {
                            title: "Error",
                            message: (errorText === "" ? functionName : errorText) + " " + result.Result,
                            okLabel: "OK"
                        },
                        ariaLabel: "Error",
                    };

                    const dialogRef = this.matDialog.open(SimpleAlertDialogComponent, dialogConfig);
                    return dialogRef.afterClosed().toPromise();
                }
            );
    }

    getAccessToken(): string
    {
        return this.shellAccessProvider.getAccessToken();
    }

    getSystemInfo(): string
    {
        // TODO - should parse JSON here, instead of offloading that responsibility onto downstream
        return this.shellAccessProvider.getSystemInfo();
    }

    getLoginViewModel(): string
    {
        return this.shellAccessProvider.getLoginViewModel();
    }

    PostMessage<T = unknown>(event: T): boolean
    {
        if (this.cefSharp)
        {
            this.cefSharp.PostMessage(event);
            return true;
        }
        return false;
    }

}
